import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FlightStatusForm } from '../../models/interfaces/flight-status-form.interface';
import { FlightStatusFormHandler } from '../handlers/flight-status-form.handler';
import { ValidationData } from '../models/validation.interfaces';
import { FlightStatusAirportService } from './flight-status-airport.service';

@Injectable()
export class FlightStatusValidationService {
  constructor(
    private formHandler: FlightStatusFormHandler,
    private airportService: FlightStatusAirportService
  ) {}

  createValidationData(form: FormGroup): ValidationData {
    const flightNumber = this.formHandler.getFlightNumber(form);
    const airportSelection = this.airportService.getSelection();
    const date = this.formHandler.getDeparture(form);

    return {
      flightNumber,
      airportSelection,
      hasValidFlightNumber: !!(
        flightNumber && /^[0-9]{1,9}$/.test(flightNumber)
      ),
      hasCompleteRoute: !!(
        airportSelection.origin && airportSelection.destination
      ),
      date
    };
  }

  validateDateField(
    date: string,
    content: FlightStatusForm,
    errors: Record<string, string>
  ): void {
    if (date === '') {
      errors['departureDate'] =
        content.errorMessages['FLIGHT_DATE_REQD']['description'];
    } else {
      errors['departureDate'] = '';
    }
  }

  validateRequiredFields(
    data: ValidationData,
    content: FlightStatusForm,
    errors: Record<string, string>
  ): void {
    this.validateDateField(data.date, content, errors);
    if (this.isEmptyForm(data)) {
      this.setFlightNumberError(content, errors);
      return;
    }

    if (this.shouldValidateRouteOnly(data)) {
      this.validateRouteOnly(data, content, errors);
      return;
    }

    // Otherwise: either flight number is valid with incomplete route,
    // or both are valid → no action needed.
  }

  private isEmptyForm(data: ValidationData): boolean {
    const { hasValidFlightNumber: hasFN, hasCompleteRoute: hasRoute } = data;
    const hasAnyAirport = !!(
      data.airportSelection.origin || data.airportSelection.destination
    );
    return !hasFN && !hasRoute && !hasAnyAirport;
  }

  private shouldValidateRouteOnly(data: ValidationData): boolean {
    const { hasValidFlightNumber: hasFN, hasCompleteRoute: hasRoute } = data;
    const hasAnyAirport = !!(
      data.airportSelection.origin || data.airportSelection.destination
    );
    // Flight number is missing/invalid, and user interacted with route (either complete or picked airports)
    return !hasFN && (hasRoute || hasAnyAirport);
  }

  validateSameAirport(
    data: ValidationData,
    content: FlightStatusForm,
    errors: { [key: string]: string }
  ): void {
    if (!content) {
      return; // Don't validate if no AEM content
    }

    if (this.hasSameAirportError(data)) {
      errors['route'] =
        content.errorMessages?.['INVALID_FROMTO']?.['description'] || '';
    }
  }

  private validateRouteOnly(
    data: ValidationData,
    content: FlightStatusForm,
    errors: { [key: string]: string }
  ): void {
    if (!content) {
      return;
    }

    this.setOriginError(data, content, errors);
    this.setDestinationError(data, content, errors);
  }

  private setFlightNumberError(
    content: FlightStatusForm,
    errors: { [key: string]: string }
  ): void {
    errors['flightNumber'] =
      content.errorMessages?.['FLIGHT_NUM_REQD']?.['description'] || '';
  }

  private setOriginError(
    data: ValidationData,
    content: FlightStatusForm,
    errors: { [key: string]: string }
  ): void {
    if (!data.airportSelection.origin) {
      errors['origin'] =
        content.errorMessages?.['FROM_CITY_REQD']?.['description'] || '';
    }
  }

  private setDestinationError(
    data: ValidationData,
    content: FlightStatusForm,
    errors: { [key: string]: string }
  ): void {
    if (!data.airportSelection.destination) {
      errors['destination'] =
        content.errorMessages?.['TO_CITY_REQD']?.['description'] || '';
    }
  }

  private hasSameAirportError(data: ValidationData): boolean {
    return (
      !data.hasValidFlightNumber &&
      !!data.airportSelection.origin &&
      !!data.airportSelection.destination &&
      data.airportSelection.origin === data.airportSelection.destination
    );
  }
}
